import { GridFrame } from 'Common/Components/GridFrame'
import { useMemo } from 'react'
import { format } from 'date-fns'
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material'
import {
  DeleteOutline,
  LockOutlined,
  LockOpenOutlined,
} from '@mui/icons-material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import { PROJECT_VISIBLITY } from 'Common/Constants/Constants'
import { Project } from './ProjectList.types'
import { projectAccessType } from '../ProjectCard/ProjectCard.types'

const COMPLETED = 'COMPLETED'
const FAILED = 'FAILED'

type ProjectRow = {
  id: string
  projectName: string
  snapshotName?: string
  snapshotLength?: number
  snapshotEndDate?: string
  snapshotReady: boolean
  // The build failed; the project opens so another snapshot can be chosen.
  snapshotFailed: boolean
  snapshotError?: string
  scenarios: number
  isPublic: boolean
  access: projectAccessType
  isOwner: boolean
  createdBy: string
  createdOn: Date
  isDeleting: boolean
}

type ProjectTableProps = {
  projects: Project[]
  userEmail: string | null
  deletingIds: string[]
  isDeleting: boolean
  accessUpdatingId: string | null
  handleOpen: (projectId: string) => void
  handleDelete: (projectId: string, projectName: string) => void
  handleAccessChange: (projectId: string, access: projectAccessType) => void
}

// public -> private takes every scenario private with it; private -> public
// hands the current scenario access to the page, which asks which scenarios
// to publish.
const nextAccess = (row: ProjectRow): projectAccessType =>
  row.isPublic
    ? {
        projectAccess: { visibility: 'private' },
        scenarioAccess: Object.fromEntries(
          Object.keys(row.access?.scenarioAccess ?? {}).map((key) => [
            key,
            'private' as const,
          ])
        ),
      }
    : {
        projectAccess: { visibility: 'public' },
        scenarioAccess: { ...row.access?.scenarioAccess },
      }

/**
 * The home page's project list as a grid: one row per project, a click on a
 * row opens it. A project whose snapshot is still building can't be opened
 * and says so in its name cell. The look is the theme's MuiDataGrid override.
 */
export const ProjectTable = ({
  projects,
  userEmail,
  deletingIds,
  isDeleting,
  accessUpdatingId,
  handleOpen,
  handleDelete,
  handleAccessChange,
}: ProjectTableProps) => {
  const rows: ProjectRow[] = useMemo(
    () =>
      projects.map((project) => {
        const snapshot = project.snapshotDetails as
          | (Project['snapshotDetails'] & { status?: string; error?: string })
          | null
        // A project with no status reads as ready, as the card did.
        const status = snapshot?.status ?? COMPLETED
        const access = project.projectAccess as projectAccessType
        return {
          id: project.projectId,
          projectName: project.projectName,
          snapshotName: snapshot?.snapshotName,
          snapshotLength: snapshot?.snapshotLength,
          snapshotEndDate: snapshot?.snapshotEndDate,
          snapshotReady: status === COMPLETED,
          snapshotFailed: status === FAILED,
          snapshotError: snapshot?.error ?? undefined,
          scenarios: project.scenariosCount ?? 0,
          isPublic:
            access?.projectAccess?.visibility === PROJECT_VISIBLITY.PUBLIC,
          access,
          isOwner: userEmail === project.email,
          createdBy: project.createdBy,
          createdOn: new Date(project.createdOn),
          isDeleting: isDeleting && deletingIds.includes(project.projectId),
        }
      }),
    [projects, userEmail, deletingIds, isDeleting]
  )

  const columns: GridColDef<ProjectRow>[] = useMemo(
    () => [
      {
        field: 'projectName',
        headerName: 'Project',
        flex: 1.4,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Tooltip
            title={
              row.isDeleting
                ? 'Deleting…'
                : row.snapshotReady
                  ? ''
                  : row.snapshotFailed
                    ? `The snapshot import failed${row.snapshotError ? `: ${row.snapshotError}` : ''}. Open the project to choose or import another.`
                    : 'The data snapshot is still being built'
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
                height: '100%',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color:
                    row.snapshotReady || row.snapshotFailed
                      ? 'var(--brand-blue-600)'
                      : 'var(--ink-500)',
                }}
              >
                {row.projectName}
              </Box>
              {row.snapshotFailed ? (
                <Chip label="Failed" color="error" />
              ) : (
                !row.snapshotReady && <Chip label="Building" color="warning" />
              )}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'snapshotName',
        headerName: 'Snapshot',
        flex: 1,
        minWidth: 160,
        valueFormatter: (value?: string) => value ?? 'No snapshot',
      },
      {
        field: 'snapshotLength',
        headerName: 'Weeks',
        type: 'number',
        width: 80,
        cellClassName: 'svp-num',
      },
      {
        field: 'snapshotEndDate',
        headerName: 'Snapshot ends',
        width: 140,
        cellClassName: 'svp-mono',
        // Sort by date, not by the "20 October 2026" text.
        sortComparator: (a?: string, b?: string) =>
          (a ? Date.parse(a) : 0) - (b ? Date.parse(b) : 0),
      },
      {
        field: 'scenarios',
        headerName: 'Scenarios',
        type: 'number',
        width: 90,
        cellClassName: 'svp-num',
      },
      {
        field: 'isPublic',
        headerName: 'Access',
        width: 90,
        valueGetter: (value: boolean) => (value ? 'Public' : 'Private'),
        renderCell: ({ row }) => (
          <Chip
            label={row.isPublic ? 'Public' : 'Private'}
            color={row.isPublic ? 'info' : 'default'}
          />
        ),
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'createdOn',
        headerName: 'Created',
        type: 'dateTime',
        width: 130,
        cellClassName: 'svp-mono',
        valueFormatter: (value: Date) =>
          isNaN(value.getTime()) ? '' : format(value, 'dd/MM/yyyy HH:mm'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableExport: true,
        align: 'right',
        renderCell: ({ row }) => {
          const accessBusy = accessUpdatingId === row.id
          return (
            // Keep the row click (open) off these buttons.
            <Box
              sx={{ display: 'flex', gap: '2px' }}
              onClick={(event) => event.stopPropagation()}
            >
              {row.isOwner && (
                <Tooltip title={row.isPublic ? 'Make private' : 'Make public'}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={
                        row.isPublic
                          ? `Make ${row.projectName} private`
                          : `Make ${row.projectName} public`
                      }
                      disabled={accessBusy}
                      onClick={() =>
                        handleAccessChange(row.id, nextAccess(row))
                      }
                    >
                      {accessBusy ? (
                        <CircularProgress size={14} />
                      ) : row.isPublic ? (
                        <LockOpenOutlined />
                      ) : (
                        <LockOutlined />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Delete project">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${row.projectName}`}
                    disabled={row.isDeleting}
                    onClick={() => handleDelete(row.id, row.projectName)}
                    sx={{ '&:hover': { color: 'var(--state-failed)' } }}
                  >
                    {row.isDeleting ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DeleteOutline />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )
        },
      },
    ],
    [accessUpdatingId, handleAccessChange, handleDelete]
  )

  return (
    <GridFrame>
      <Table
        rows={rows}
        columns={columns}
        showToolbar
        pagination
        initialState={{
          sorting: { sortModel: [{ field: 'createdOn', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        onRowClick={({ row }) => {
          if ((row.snapshotReady || row.snapshotFailed) && !row.isDeleting)
            handleOpen(row.id)
        }}
        getRowClassName={({ row, indexRelativeToCurrentPage }) =>
          [
            indexRelativeToCurrentPage % 2 === 0 ? 'row--even' : 'row--odd',
            (row.snapshotReady || row.snapshotFailed) && !row.isDeleting
              ? 'row--openable'
              : '',
            row.isDeleting ? 'row--deleting' : '',
          ].join(' ')
        }
        sx={{
          height: '100%',
          '& .row--openable': { cursor: 'pointer' },
          '& .row--deleting': { opacity: 'var(--opacity-faded)' },
        }}
      />
    </GridFrame>
  )
}
